import {match, Pattern} from 'ts-pattern';

import type {
  CustomerInsightsHistoryGroup,
  CustomerInsightsSection,
} from '../types/CustomerInsightsViewModel';
import {takeNewestHistory} from './takeNewestHistory';

type HistorySection = CustomerInsightsSection<CustomerInsightsHistoryGroup>;

const getHistoryData = (section: HistorySection) =>
  match(section)
    .with({status: 'ready'}, ({data}) => [data])
    .with({status: 'degraded'}, ({data}) => [data])
    .otherwise(() => []);

const getFailedRetries = (section: HistorySection) =>
  match(section)
    .with({status: 'error'}, ({retry}) => [retry])
    .with({status: 'degraded'}, ({retry}) => [retry])
    .otherwise(() => []);

const mergeHistoryData = (
  sections: HistorySection[],
  requestedRowsLimit: number,
  expandRequestedRowsLimit: VoidFunction,
  resetRequestedRowsLimit: VoidFunction
) => {
  const groups = sections.flatMap(getHistoryData);
  const loadedItems = groups.flatMap((group) => group.items);
  const knownTotals = groups.flatMap(({total}) =>
    match(total)
      .with(Pattern.number, (knownTotal) => [knownTotal])
      .otherwise(() => [])
  );
  const total = match({groupCount: groups.length, knownTotalCount: knownTotals.length})
    .when(
      ({groupCount, knownTotalCount}) => groupCount > 0 && knownTotalCount === groupCount,
      () => knownTotals.reduce((sum, groupTotal) => sum + groupTotal, 0)
    )
    .otherwise(() => undefined);
  const data = takeNewestHistory(loadedItems, total, requestedRowsLimit);
  const loadMoreActions = groups.flatMap((group) =>
    group.hasMore && group.loadMore ? [group.loadMore] : []
  );
  const showLessActions = groups.flatMap((group) => (group.showLess ? [group.showLess] : []));
  const isPaginationExpanded = groups.some((group) => group.isPaginationExpanded);
  const hasHiddenLoadedItems = data.items.length < loadedItems.length;
  const hasMore = match(data.total)
    .with(
      Pattern.number,
      (knownTotal) =>
        data.items.length < knownTotal && (hasHiddenLoadedItems || loadMoreActions.length > 0)
    )
    .otherwise(() => hasHiddenLoadedItems || loadMoreActions.length > 0);

  return hasMore || isPaginationExpanded || groups.some((group) => group.isLoadingMore)
    ? {
        ...data,
        hasMore,
        isPaginationExpanded,
        isLoadingMore: groups.some((group) => group.isLoadingMore),
        loadMore: () => {
          expandRequestedRowsLimit();
          loadMoreActions.forEach((loadMore) => loadMore());
        },
        showLess: () => {
          resetRequestedRowsLimit();
          showLessActions.forEach((showLess) => showLess());
        },
      }
    : data;
};

export const combineCustomerInsightsHistorySections = (
  first: HistorySection,
  second: HistorySection,
  requestedRowsLimit: number,
  expandRequestedRowsLimit: VoidFunction,
  resetRequestedRowsLimit: VoidFunction
): HistorySection => {
  const sections = [first, second];
  const availableSections = sections.filter((section) => section.status !== 'omitted');
  const data = mergeHistoryData(
    sections,
    requestedRowsLimit,
    expandRequestedRowsLimit,
    resetRequestedRowsLimit
  );
  const failedRetries = sections.flatMap(getFailedRetries);
  const retry = () => failedRetries.forEach((refetch) => refetch());
  const state = {
    hasAvailableSource: availableSections.length > 0,
    hasData: data.items.length > 0,
    hasEmpty: availableSections.some((section) => section.status === 'empty'),
    hasError: failedRetries.length > 0,
    hasLoading: availableSections.some((section) => section.status === 'loading'),
  };

  return match(state)
    .returnType<HistorySection>()
    .with({hasAvailableSource: false}, () => ({status: 'omitted'}))
    .with({hasError: true, hasData: true}, () => ({status: 'degraded', data, retry}))
    .with({hasError: true, hasEmpty: true}, () => ({status: 'degraded', data, retry}))
    .with({hasData: true}, () => ({status: 'ready', data}))
    .with({hasLoading: true}, () => ({status: 'loading'}))
    .with({hasError: true}, () => ({status: 'error', retry}))
    .otherwise(() => ({status: 'empty'}));
};
